import React from 'react';
import { Card, Chip, ContentCard, FilterSetController, Table, Icon } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../components/loadingCard';
import Style from './resaleStats.module.css';

export default function ResaleStatsCard({ stats }) {
    const { t } = useTranslation();
    const [search, setSearch] = React.useState(null);

    const statistics = React.useMemo(() => {
        if (!stats) {
            return null;
        }

        return stats.find(groupStats => groupStats.name === 'sales_by_resale_stats').value;
    }, [stats]);

    const matchSearch = React.useCallback((value, search) => {
        const reg = new RegExp(search, 'gi');
        return reg.test(value);
    });

    const filterNodeHierarchy = React.useCallback(
        (node, search) => {
            // search for a specific group name or a specific rate
            if (matchSearch(node.name, search) || node.rates.find(rate => matchSearch(rate.name, search))) {
                return node;
            }

            // Get full tree for a specific rate
            let filteredNodeTree = { ...node };
            filteredNodeTree.children = node.children.map(node => filterNodeHierarchy(node, search)).filter(Boolean);

            if (!filteredNodeTree.children.length) {
                return null;
            }
            return filteredNodeTree;
        },
        [matchSearch]
    );

    const filteredRates = React.useMemo(() => {
        if (!search || search === '') {
            return statistics;
        }
        return {
            root: statistics.root.map(node => filterNodeHierarchy(node, search)).filter(Boolean),
            rates_without_group: statistics.rates_without_group.filter(rate => matchSearch(rate.name, search))
        };
    }, [statistics, search, filterNodeHierarchy, matchSearch]);

    return (
        <div>
            <Card separator="false">
                <ContentCard>
                    <FilterSetController
                        total={statistics?.total_rates}
                        isLoadingTotal={!statistics}
                        labelTotal={t('ticket.rates.filters.label_total_rates', { count: statistics?.total_rates || 0 })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            onChange: (synth, value) => {
                                setSearch(value);
                            },
                            handleReset: () => {
                                setSearch(null);
                            },
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: search || ''
                        }}
                        onChange={() => {}}
                    />
                    <LoadingCard loading={!filteredRates}>
                        <div className={Style['resale-stats-table']}>
                            <Chip primary icon="lock" text={t('ticket.attendees_stats.resale.privateResale')} />
                            <Table useNew>
                                <Table.THead>
                                    <Table.Tr>
                                        <th>{t('ticket.common.rate')}</th>
                                        <th className={Style['stat-column']}>{t('ticket.attendees_stats.resale.on_sale')}</th>
                                        <th className={Style['stat-column']}>{t('ticket.attendees_stats.resale.sale_in_progress')}</th>
                                        <th className={Style['stat-column']}>{t('ticket.attendees_stats.resale.sold')}</th>
                                    </Table.Tr>
                                </Table.THead>
                                <Table.TBody>
                                    <RateRows stats={filteredRates?.rates_without_group} />
                                    {filteredRates?.root.map((grp, index) => {
                                        return <TableGroup key={index} stats={grp} />;
                                    })}
                                </Table.TBody>
                            </Table>
                        </div>
                    </LoadingCard>
                </ContentCard>
            </Card>
        </div>
    );
}

function RateRows({ stats, depth = 0 }) {
    const statuses = ['on_sale', 'sale_in_progress', 'sold'];

    return stats.map(rate => {
        return (
            <Table.Tr key={rate.id}>
                <Table.Td>
                    <span style={{ marginLeft: `${20 * depth}px` }}>{rate.name}</span>
                </Table.Td>
                {statuses.map((status, index) => (
                    <Table.Td key={index}>
                        <div className={Style['rate-status']}>
                            <span>{rate[status]}</span>
                            <Chip primary icon="lock" text={rate[`${status}_private`] || 0} />
                        </div>
                    </Table.Td>
                ))}
            </Table.Tr>
        );
    });
}

function TableGroup({ stats, depth = 0 }) {
    const [showChildren, setShowChildren] = React.useState(true);

    if (!stats.children.length && !stats.rates.length) {
        // Prevent displaying empty groups
        return null;
    }

    const computeGroupValue = React.useCallback((group, name, depth = 0) => {
        // Recursively compute stats for groups and subgroups
        if (depth > 0 || group.children.length) {
            return group.rates.reduce((acc, val) => val[name] + acc, 0) + group.children.reduce((acc, grp) => acc + computeGroupValue(grp, name, depth + 1), 0);
        }
        return group.rates.reduce((acc, val) => val[name] + acc, 0);
    }, []);

    return (
        <>
            <Table.Tr className={`${Style['resale-stats-icons']} ${depth === 0 ? Style['resale-stats-rate-group'] : Style['resale-stats-rate-sub-group']}`}>
                <Table.Td>
                    <span className={Style['resale-stats-toggle']} onClick={() => setShowChildren(!showChildren)}>
                        {depth > 0 && <Icon name="link-group" size="small" />}
                        <Icon name={showChildren ? 'folder-open' : 'folder-closed'} />
                        <b>{stats.name}</b>
                    </span>
                </Table.Td>
                <Table.Td>{computeGroupValue(stats, 'on_sale')}</Table.Td>
                <Table.Td>{computeGroupValue(stats, 'sale_in_progress')}</Table.Td>
                <Table.Td>{computeGroupValue(stats, 'sold')}</Table.Td>
            </Table.Tr>
            {showChildren && (
                <>
                    <RateRows stats={stats.rates} depth={depth + 1} />
                    {stats.children?.map(grp => {
                        return <TableGroup key={grp.id} stats={grp} depth={depth + 1} />;
                    })}
                </>
            )}
        </>
    );
}
